import { PartySocket } from 'partysocket';
import { getOrCreateUserToken } from './coop.svelte';

const PARTYKIT_HOST =
	(import.meta.env.VITE_PARTYKIT_HOST as string | undefined) ??
	(import.meta.env.DEV ? 'localhost:1999' : 'bubble-games-coop.loljoshie.partykit.dev');

/** Open a short-lived socket to the directory party, run the action, close. */
function withDirectory<T>(
	send: (socket: PartySocket) => void,
	handle: (msg: unknown, resolve: (v: T) => void, reject: (err: Error) => void) => void,
	timeoutMs = 8000
): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const socket = new PartySocket({
			host: PARTYKIT_HOST,
			party: 'directory',
			room: 'global'
		});

		let settled = false;
		const finish = (fn: () => void) => {
			if (settled) return;
			settled = true;
			fn();
			socket.close();
		};

		const timer = setTimeout(() => {
			finish(() => reject(new Error('Timed out connecting to server')));
		}, timeoutMs);

		socket.addEventListener('open', () => send(socket));
		socket.addEventListener('error', (e: Event) => {
			console.error('[directory] socket error', {
				host: PARTYKIT_HOST,
				url: (socket as unknown as { url?: string }).url,
				readyState: (socket as unknown as { readyState?: number }).readyState,
				event: e
			});
			clearTimeout(timer);
			finish(() => reject(new Error('Connection error')));
		});
		socket.addEventListener('close', (e: Event) => {
			const ce = e as CloseEvent;
			if (!settled) {
				console.error('[directory] socket closed before settling', {
					code: ce.code,
					reason: ce.reason,
					wasClean: ce.wasClean
				});
			}
		});
		socket.addEventListener('message', (event) => {
			let msg: unknown;
			try {
				msg = JSON.parse(event.data);
			} catch {
				return;
			}
			handle(
				msg,
				(v) => {
					clearTimeout(timer);
					finish(() => resolve(v));
				},
				(err) => {
					clearTimeout(timer);
					finish(() => reject(err));
				}
			);
		});
	});
}

export type ClaimResult = { ok: true; username: string } | { ok: false; error: string };

export async function claimUsername(userId: string, username: string): Promise<ClaimResult> {
	try {
		return await withDirectory<ClaimResult>(
			(socket) => socket.send(JSON.stringify({ type: 'claim', userId, username, token: getOrCreateUserToken() })),
			(raw, resolve) => {
				const msg = raw as { type?: string; username?: string; message?: string };
				if (msg.type === 'claimed' && msg.username) {
					resolve({ ok: true, username: msg.username });
				} else if (msg.type === 'error') {
					resolve({ ok: false, error: msg.message ?? 'Could not claim username' });
				}
			}
		);
	} catch (err) {
		return { ok: false, error: (err as Error).message };
	}
}

export async function renewUsername(userId: string): Promise<void> {
	try {
		const host = (import.meta.env.VITE_PARTYKIT_HOST as string | undefined) ??
			(import.meta.env.DEV ? 'localhost:1999' : 'bubble-games-coop.loljoshie.partykit.dev');
		const base = host.startsWith('localhost') ? `http://${host}` : `https://${host}`;
		await fetch(`${base}/parties/directory/global`, {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ type: 'renew', userId, token: getOrCreateUserToken() })
		});
	} catch {
		/* best effort — don't block app launch */
	}
}

export async function releaseUsername(userId: string): Promise<boolean> {
	// Use the WS `release` path (token-authenticated, owner-only) rather than
	// the admin `force-release` HTTP endpoint, which now requires the server
	// RPC secret.
	try {
		const ok = await withDirectory<boolean>(
			(socket) => socket.send(JSON.stringify({ type: 'release', userId, token: getOrCreateUserToken() })),
			(raw, resolve) => {
				const msg = raw as { type?: string };
				if (msg.type === 'released') resolve(true);
				else if (msg.type === 'error') resolve(false);
			}
		);
		return ok;
	} catch {
		return false;
	}
}

export async function lookupUsername(username: string): Promise<string | null> {
	try {
		return await withDirectory<string | null>(
			(socket) => socket.send(JSON.stringify({ type: 'lookup', username })),
			(raw, resolve) => {
				const msg = raw as { type?: string; userId?: string | null };
				if (msg.type === 'lookup') resolve(msg.userId ?? null);
			}
		);
	} catch {
		return null;
	}
}

function directoryUrl(): string {
	const host = (import.meta.env.VITE_PARTYKIT_HOST as string | undefined) ??
		(import.meta.env.DEV ? 'localhost:1999' : 'bubble-games-coop.loljoshie.partykit.dev');
	const base = host.startsWith('localhost') ? `http://${host}` : `https://${host}`;
	return `${base}/parties/directory/global`;
}

async function directoryPost<T>(body: Record<string, unknown>): Promise<T | null> {
	try {
		const res = await fetch(directoryUrl(), {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(body)
		});
		if (!res.ok) return null;
		return (await res.json()) as T;
	} catch {
		return null;
	}
}

export async function setCloudPin(userId: string, pin: string): Promise<{ ok: boolean; error?: string }> {
	const res = await directoryPost<{ ok: boolean; error?: string }>({ type: 'set-pin', userId, pin, token: getOrCreateUserToken() });
	return res ?? { ok: false, error: 'Network error' };
}

/**
 * Drop the directory's stored token hash for this userId. Called on
 * Reset/Delete so a later token regeneration can't wedge every authed request
 * at 401 — the next call re-adopts the current token cleanly. Best-effort.
 */
export async function clearIdentityToken(userId: string): Promise<{ ok: boolean }> {
	const res = await directoryPost<{ ok: boolean }>({ type: 'clear-token', userId, token: getOrCreateUserToken() });
	return { ok: !!res?.ok };
}

export async function cloudSave(userId: string, pin: string, data: unknown): Promise<{ ok: boolean; error?: string; syncedAt?: number }> {
	const res = await directoryPost<{ ok: boolean; error?: string; syncedAt?: number }>({ type: 'cloud-save', userId, pin, data, token: getOrCreateUserToken() });
	return res ?? { ok: false, error: 'Network error' };
}

export type CloudLoadResult =
	| { ok: true; userId: string; data: Record<string, unknown>; syncedAt: number | null }
	| { ok: false; error: string };

export async function cloudLoad(username: string, pin: string): Promise<CloudLoadResult> {
	const res = await directoryPost<CloudLoadResult>({ type: 'cloud-load', username, pin });
	return res ?? { ok: false, error: 'Network error' };
}

export async function cloudStatus(userId: string): Promise<{ hasPin: boolean; syncedAt: number | null }> {
	const res = await directoryPost<{ hasPin: boolean; syncedAt: number | null }>({ type: 'cloud-status', userId, token: getOrCreateUserToken() });
	return res ?? { hasPin: false, syncedAt: null };
}

export async function cloudDelete(userId: string): Promise<boolean> {
	const res = await directoryPost<{ ok: boolean }>({ type: 'cloud-delete', userId, token: getOrCreateUserToken() });
	return res?.ok ?? false;
}

/** What username (if any) is this userId currently claiming? */
export async function whoami(userId: string): Promise<string | null> {
	try {
		return await withDirectory<string | null>(
			(socket) => socket.send(JSON.stringify({ type: 'whoami', userId })),
			(raw, resolve) => {
				const msg = raw as { type?: string; username?: string | null };
				if (msg.type === 'whoami') resolve(msg.username ?? null);
			}
		);
	} catch {
		return null;
	}
}
